export default class Logger {
    static #COLORS = {
        BLUE: "#00F",
        PURPLE: "#a267e7",
        GREEN: "#25e80e",
        CYAN: "#12d97f",
        TURQUOISE: "#12d9d2",
        YELLOW: "#f3be06",
        RED: "#F00"
    }


    /** @member {string} **/
    #color;

    /** @member {string} **/
    #prefix;

    /** @member {boolean} **/
    #isStarted = false;

    /** @member {boolean} **/
    #debugFlag = false;

    /** @member {boolean} **/
    #errorFlag = false;

    /**
     * Creates a logger instance with the given cookie used to determine if anything other than errors are displayed.
     * If the debug cookie is not set to "1" then all log messages will be supressed except for errors. You can set a
     * prefix that will be added to top-level console messages automatically and a color that will be used to style it.
     *
     * @param {string} prefix
     * @param {string} hexColor
     * @param {string} cookieName
     */
    constructor(prefix, hexColor = "#00F", cookieName = "debug") {
        this.#prefix = prefix;
        this.#color = hexColor;
        this.#debugFlag = this.#getCookie(cookieName) == 1
    }

    /**
     * Retrieves a cookie value with a given name.
     *
     * @param {string} name
     * @returns {string}
     */
    #getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    }

    /**
     * Internal function for standardizing output for some of the more generic logging functions
     *
     * @param {string} method
     * @param {boolean} useCookie
     * @param {*} args
     */
    #output(method, useCookie, ...args) {
        if(!useCookie || this.#debugFlag) {
            if(!this.#isStarted) {
                console[method].call(window, `[%c${this.#prefix}%c]`, `color: ${this.#color};`, "color: inherit;", ...args);
            } else {
                console[method].call(window, ...args);
            }
        }
    }

    /**
     * Creates a standard console log message.
     *
     * Displays nothing if the debug flag is off.
     *
     * @see console.log
     * @param {*} args
     * @returns {Logger}
     */
    log(...args) {
        this.#output("log", true, ...args);
        return this;
    }

    /**
     * Creates a warning console log message.
     *
     * Displays nothing if the debug flag is off.
     *
     * @see console.log
     * @param {*} args
     * @returns {Logger}
     */
    warn(...args) {
        this.#output("warn", true, ...args);
        return this;
    }

    /**
     * Displays an error message in the console, along with a stack trace.
     *
     * Will display regardless of cookie status, no matter if it is nested inside a group. If inside a log entry group
     * a warning will be raised at top level while the debug flag is on.
     *
     * @see console.error
     * @param {*} args
     * @returns {Logger}
     */
    error(...args) {
        this.#errorFlag = true;
        this.#output("error", false, ...args);
        return this;
    }

    /**
     * Displays a table of values from a JS object or array.
     *
     * Displays nothing if the debug flag is off.
     *
     * @see console.table
     * @param {object|Array} data
     * @param {Array} columns
     * @returns {Logger}
     */
    table(data, columns = null) {
        if(this.#debugFlag) {
            console.table(data, columns);
        }

        return this;
    }

    /**
     * Creates a log entry specifically for displaying JS objects.
     *
     * Displays nothing if the debug flag is off.
     *
     * @see console.dir
     * @param {object} object
     * @returns {Logger}
     */
    dir(object) {
        if(this.#debugFlag) {
            console.dir(object);
        }

        return this;
    }

    /**
     * Creates an expandable area that will contain all logged messages until {@see Logger.end()} is called. The label
     * will be prefixed with the global [tag] for this instance, using the default color.
     *
     * This grouping will display an [Warning] message at the top level if any calls to {@see Logger.error()} are made
     * after start() is called and before end() is called. This is to increase visibility of any errors hidden within
     * collapsed groups.
     *
     * Displays nothing if the debug flag is off
     *
     * @param {string} label
     * @returns {Logger}
     */
    start(label) {
        if(this.#debugFlag && !this.#isStarted) {
            console.groupCollapsed(`[%c${this.#prefix}%c]`, `color: ${this.#color};`, "color: inherit;", label);
            this.#isStarted = true;
        }

        return this;
    }

    /**
     * Ends the current log entry, called after {@see Logger.start()}.
     *
     * Displays nothing if the debug flag is off.
     *
     * @returns {Logger}
     */
    end() {
        if(this.#debugFlag && this.#isStarted) {
            console.groupEnd();
            this.#isStarted = false;

            if(this.#errorFlag) {
                console.warn(`[%c${this.#prefix}%c][%cWarning%c]`, `color: ${this.#color};`, "color: inherit;", `color: ${this.COLOR.RED};`, "color: inherit;", "One or more errors occured in the previous group.");
            }
        }

        this.#errorFlag = false;

        return this;
    }

    /**
     * Creates a subgroup with a colored prefix [tag] of your choice. Defaults to the same color as the main prefix.
     *
     * Displays nothing if the debug flag is off.
     *
     * @param {string} prefix
     * @param {string} label
     * @param {string} color
     * @returns {Logger}
     */
    groupStart(prefix, label, color = null) {
        if(this.#debugFlag) {
            color = color || this.#color;
            console.groupCollapsed(`[%c${prefix}%c]`, `color: ${color};`, "color: inherit;", label);
        }

        return this;
    }

    /**
     * Ends the currently active group.
     *
     * Displays nothing if the debug flag is off.
     *
     * @returns {Logger}
     */
    groupEnd() {
        if(this.#debugFlag) {
            console.groupEnd();
        }

        return this;
    }

    /**
     * Starts a new timer with a given name.
     *
     * Displays nothing if the debug flag is off.
     *
     * @param {string} name
     * @returns {Logger}
     */
    timerStart(name = "Timer") {
        if(this.#debugFlag) {
            console.time(name)
        }

        return this;
    }

    /**
     * Ends a currently running timer with the given name.
     *
     * Displays nothing if the debug flag is off.
     *
     * @param {string} name
     * @returns {Logger}
     */
    timerEnd(name = "Time") {
        if(this.#debugFlag) {
            console.timeEnd(name)
        }

        return this;
    }

    get COLOR() {
        return Logger.#COLORS
    }

    get isDebug() {
        return this.#debugFlag;
    }
}